import { INode, ITypeNode } from '../Context/FormBuilderContext';
import { ImageFormData } from '../schemas/image.schema';
import { InputFormData } from '../schemas/input.schema';
import { ListFormData } from '../schemas/list.schema';
import { OutputFormData } from '../schemas/output.schema';
import { ShortTextFormData } from '../schemas/shortText.schema';
import { SubmitFormData } from '../schemas/submit.schema';
import { TitleFormData } from '../schemas/title.schema';
import {
    defaultImageNodeData,
    defaultInputNodeData,
    defaultListNodeData,
    defaultOutputNodeData,
    defaultShortTextNodeData,
    defaultSubmitNodeData,
    defaultTitleNodeData,
} from './nodeDefaults';

type FormToNodeTransformer<T> = (formData: T, node: INode) => INode['data'];
type NodeToFormTransformer<T> = (node: INode) => T;

export function titleNodeToForm(node: INode): TitleFormData {
  const styles = node.data.styles;
  const defaults = defaultTitleNodeData.styles;
  const titleDefaults = defaults.title!;

  const result: TitleFormData = {
    title: node.data.title ?? defaultTitleNodeData.title!,
    fontFamily: styles?.title?.fontFamily ?? titleDefaults.fontFamily!,
    fontSize: styles?.title?.fontSize ?? titleDefaults.fontSize!,
    isBold: styles?.title?.isBold ?? titleDefaults.isBold!,
    isItalic: styles?.title?.isItalic ?? titleDefaults.isItalic!,
    isUnderlined: styles?.title?.isUnderlined ?? titleDefaults.isUnderlined!,
    textAlign: (styles?.title?.textAlign ?? titleDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    titleColor: styles?.title?.color ?? titleDefaults.color!,
    backgroundColor: styles?.backgroundColor ?? defaults.backgroundColor,
    borderColor: styles?.borderColor ?? defaults.borderColor,
    borderRadius: styles?.borderRadius ?? defaults.borderRadius,
    borderWeight: styles?.borderWeight ?? defaults.borderWeight,
    verticalAlign: (styles?.verticalAlign ?? defaults.verticalAlign) as 'top' | 'center' | 'bottom',
    padding: styles?.padding ?? defaults.padding,
  };

  return result;
}

export function titleFormToNode(formData: TitleFormData, node: INode): INode['data'] {
  return {
    ...node.data,
    title: formData.title,
    styles: {
      ...node.data.styles,
      title: {
        fontFamily: formData.fontFamily,
        fontSize: formData.fontSize,
        isBold: formData.isBold,
        isItalic: formData.isItalic,
        isUnderlined: formData.isUnderlined,
        textAlign: formData.textAlign,
        color: formData.titleColor,
      },
      backgroundColor: formData.backgroundColor,
      borderColor: formData.borderColor,
      borderRadius: formData.borderRadius,
      borderWeight: formData.borderWeight,
      verticalAlign: formData.verticalAlign,
      padding: formData.padding,
    },
  };
}

export function shortTextNodeToForm(node: INode): ShortTextFormData {
  const styles = node.data.styles;
  const defaults = defaultShortTextNodeData.styles;
  const shortTextDefaults = defaults.shortText!;

  const result: ShortTextFormData = {
    text: node.data.text ?? defaultShortTextNodeData.text!,
    fontFamily: styles?.shortText?.fontFamily ?? shortTextDefaults.fontFamily!,
    fontSize: styles?.shortText?.fontSize ?? shortTextDefaults.fontSize!,
    isBold: styles?.shortText?.isBold ?? shortTextDefaults.isBold!,
    isItalic: styles?.shortText?.isItalic ?? shortTextDefaults.isItalic!,
    isUnderlined: styles?.shortText?.isUnderlined ?? shortTextDefaults.isUnderlined!,
    textAlign: (styles?.shortText?.textAlign ?? shortTextDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    shortTextColor: styles?.shortText?.color ?? shortTextDefaults.color!,
    backgroundColor: styles?.backgroundColor ?? defaults.backgroundColor,
    borderColor: styles?.borderColor ?? defaults.borderColor,
    borderRadius: styles?.borderRadius ?? defaults.borderRadius,
    borderWeight: styles?.borderWeight ?? defaults.borderWeight,
    verticalAlign: (styles?.verticalAlign ?? defaults.verticalAlign) as 'top' | 'center' | 'bottom',
    padding: styles?.padding ?? defaults.padding,
  };

  return result;
}

export function shortTextFormToNode(formData: ShortTextFormData, node: INode): INode['data'] {
  return {
    ...node.data,
    text: formData.text,
    styles: {
      ...node.data.styles,
      shortText: {
        fontFamily: formData.fontFamily,
        fontSize: formData.fontSize,
        isBold: formData.isBold,
        isItalic: formData.isItalic,
        isUnderlined: formData.isUnderlined,
        textAlign: formData.textAlign,
        color: formData.shortTextColor,
      },
      backgroundColor: formData.backgroundColor,
      borderColor: formData.borderColor,
      borderRadius: formData.borderRadius,
      borderWeight: formData.borderWeight,
      verticalAlign: formData.verticalAlign,
      padding: formData.padding,
    },
  };
}
  
export function inputNodeToForm(node: INode): InputFormData {
  const styles = node.data.styles;
  const defaults = defaultInputNodeData.styles;
  const titleDefaults = defaults.title!;
  const subtitleDefaults = defaults.subtitle!;

  const result: InputFormData = {
    title: node.data.title ?? defaultInputNodeData.title!,
    subtitle: node.data.subtitle ?? defaultInputNodeData.subtitle!,
    placeholder: node.data.placeholder ?? '',
    varDataInputId: node.data.varDataInputId,
    fieldType: (node.data.fieldType ?? defaultInputNodeData.fieldType!) as 'standard' | 'editor' | 'data' | 'both' | 'upload',
    requiredField: node.data.requiredField ?? defaultInputNodeData.requiredField!,
    allowedFiles: node.data.allowedFiles ?? defaultInputNodeData.allowedFiles!,
    minRows: node.data.minRows ?? defaultInputNodeData.minRows!,
    fontFamily: styles?.title?.fontFamily ?? titleDefaults.fontFamily!,
    fontSize: styles?.title?.fontSize ?? titleDefaults.fontSize!,
    isBold: styles?.title?.isBold ?? titleDefaults.isBold!,
    isItalic: styles?.title?.isItalic ?? titleDefaults.isItalic!,
    isUnderlined: styles?.title?.isUnderlined ?? titleDefaults.isUnderlined!,
    textAlign: (styles?.title?.textAlign ?? titleDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    titleColor: styles?.title?.color ?? titleDefaults.color!,
    subtitleFontFamily: styles?.subtitle?.fontFamily ?? subtitleDefaults.fontFamily!,
    subtitleFontSize: styles?.subtitle?.fontSize ?? subtitleDefaults.fontSize!,
    isSubtitleBold: styles?.subtitle?.isBold ?? subtitleDefaults.isBold!,
    isSubtitleItalic: styles?.subtitle?.isItalic ?? subtitleDefaults.isItalic!,
    isSubtitleUnderlined: styles?.subtitle?.isUnderlined ?? subtitleDefaults.isUnderlined!,
    subtitleTextAlign: (styles?.subtitle?.textAlign ?? subtitleDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    subtitleColor: styles?.subtitle?.color ?? subtitleDefaults.color!,
    backgroundColor: styles?.backgroundColor ?? defaults.backgroundColor,
    borderColor: styles?.borderColor ?? defaults.borderColor,
    borderRadius: styles?.borderRadius ?? defaults.borderRadius,
    borderWeight: styles?.borderWeight ?? defaults.borderWeight,
    verticalAlign: (styles?.verticalAlign ?? defaults.verticalAlign) as 'top' | 'center' | 'bottom',
    padding: styles?.padding ?? defaults.padding,
  };

  return result;
}

export function inputFormToNode(formData: InputFormData, node: INode): INode['data'] {
  return {
    ...node.data,
    title: formData.title,
    subtitle: formData.subtitle,
    placeholder: formData.placeholder,
    varDataInputId: formData.varDataInputId,
    fieldType: formData.fieldType,
    requiredField: formData.requiredField,
    allowedFiles: formData.allowedFiles,
    minRows: formData.minRows,
    styles: {
      ...node.data.styles,
      title: {
        fontFamily: formData.fontFamily,
        fontSize: formData.fontSize,
        isBold: formData.isBold,
        isItalic: formData.isItalic,
        isUnderlined: formData.isUnderlined,
        textAlign: formData.textAlign,
        color: formData.titleColor,
      },
      subtitle: {
        fontFamily: formData.subtitleFontFamily,
        fontSize: formData.subtitleFontSize,
        isBold: formData.isSubtitleBold,
        isItalic: formData.isSubtitleItalic,
        isUnderlined: formData.isSubtitleUnderlined,
        textAlign: formData.subtitleTextAlign,
        color: formData.subtitleColor,
      },
      backgroundColor: formData.backgroundColor,
      borderColor: formData.borderColor,
      borderRadius: formData.borderRadius,
      borderWeight: formData.borderWeight,
      verticalAlign: formData.verticalAlign,
      padding: formData.padding,
    },
  };
}

export function listNodeToForm(node: INode): ListFormData {
  const styles = node.data.styles;
  const defaults = defaultListNodeData.styles;
  const titleDefaults = defaults.title!;
  const questionDefaults = defaults.question!;

  const result: ListFormData = {
    title: node.data.title ?? defaultListNodeData.title!,
    question: node.data.question ?? defaultListNodeData.question!,
    typeQuestion: (node.data.typeQuestion ?? defaultListNodeData.typeQuestion!) as 'dropdown' | 'single' | 'multiple',
    varDataInputId: node.data.varDataInputId,
    requiredField: node.data.requiredField ?? defaultListNodeData.requiredField!,
    listItems: node.data.listItems ?? defaultListNodeData.listItems!,
    fontFamily: styles?.title?.fontFamily ?? titleDefaults.fontFamily!,
    fontSize: styles?.title?.fontSize ?? titleDefaults.fontSize!,
    isBold: styles?.title?.isBold ?? titleDefaults.isBold!,
    isItalic: styles?.title?.isItalic ?? titleDefaults.isItalic!,
    isUnderlined: styles?.title?.isUnderlined ?? titleDefaults.isUnderlined!,
    textAlign: (styles?.title?.textAlign ?? titleDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    titleColor: styles?.title?.color ?? titleDefaults.color!,
    questionFontFamily: styles?.question?.fontFamily ?? questionDefaults.fontFamily!,
    questionFontSize: styles?.question?.fontSize ?? questionDefaults.fontSize!,
    isQuestionBold: styles?.question?.isBold ?? questionDefaults.isBold!,
    isQuestionItalic: styles?.question?.isItalic ?? questionDefaults.isItalic!,
    isQuestionUnderlined: styles?.question?.isUnderlined ?? questionDefaults.isUnderlined!,
    questionTextAlign: (styles?.question?.textAlign ?? questionDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    questionColor: styles?.question?.color ?? questionDefaults.color!,
    backgroundColor: styles?.backgroundColor ?? defaults.backgroundColor,
    borderColor: styles?.borderColor ?? defaults.borderColor,
    borderRadius: styles?.borderRadius ?? defaults.borderRadius,
    borderWeight: styles?.borderWeight ?? defaults.borderWeight,
    verticalAlign: (styles?.verticalAlign ?? defaults.verticalAlign) as 'top' | 'center' | 'bottom',
    padding: styles?.padding ?? defaults.padding,
  };

  return result;
}

export function listFormToNode(formData: ListFormData, node: INode): INode['data'] {
  return {
    ...node.data,
    title: formData.title,
    question: formData.question,
    typeQuestion: formData.typeQuestion,
    varDataInputId: formData.varDataInputId,
    requiredField: formData.requiredField,
    listItems: formData.listItems,
    styles: {
      ...node.data.styles,
      title: {
        fontFamily: formData.fontFamily,
        fontSize: formData.fontSize,
        isBold: formData.isBold,
        isItalic: formData.isItalic,
        isUnderlined: formData.isUnderlined,
        textAlign: formData.textAlign,
        color: formData.titleColor,
      },
      question: {
        fontFamily: formData.questionFontFamily,
        fontSize: formData.questionFontSize,
        isBold: formData.isQuestionBold,
        isItalic: formData.isQuestionItalic,
        isUnderlined: formData.isQuestionUnderlined,
        textAlign: formData.questionTextAlign,
        color: formData.questionColor,
      },
      backgroundColor: formData.backgroundColor,
      borderColor: formData.borderColor,
      borderRadius: formData.borderRadius,
      borderWeight: formData.borderWeight,
      verticalAlign: formData.verticalAlign,
      padding: formData.padding,
    },
  };
}

export function outputNodeToForm(node: INode): OutputFormData {
  const styles = node.data.styles;
  const defaults = defaultOutputNodeData.styles;
  const titleDefaults = defaults.title!;
  const subtitleDefaults = defaults.subtitle!;

  const result: OutputFormData = {
    title: node.data.title ?? defaultOutputNodeData.title!,
    subtitle: node.data.subtitle ?? defaultOutputNodeData.subtitle!,
    varDataOutputId: node.data.varDataOutputId,
    fieldType: (node.data.fieldType ?? defaultOutputNodeData.fieldType!) as 'standard' | 'editor' | 'data' | 'both' | 'upload',
    fontFamily: styles?.title?.fontFamily ?? titleDefaults.fontFamily!,
    fontSize: styles?.title?.fontSize ?? titleDefaults.fontSize!,
    isBold: styles?.title?.isBold ?? titleDefaults.isBold!,
    isItalic: styles?.title?.isItalic ?? titleDefaults.isItalic!,
    isUnderlined: styles?.title?.isUnderlined ?? titleDefaults.isUnderlined!,
    textAlign: (styles?.title?.textAlign ?? titleDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    titleColor: styles?.title?.color ?? titleDefaults.color!,
    subtitleColor: styles?.subtitle?.color ?? subtitleDefaults.color!,
    subtitleFontFamily: styles?.subtitle?.fontFamily ?? subtitleDefaults.fontFamily!,
    subtitleFontSize: styles?.subtitle?.fontSize ?? subtitleDefaults.fontSize!,
    isSubtitleBold: styles?.subtitle?.isBold ?? subtitleDefaults.isBold!,
    isSubtitleItalic: styles?.subtitle?.isItalic ?? subtitleDefaults.isItalic!,
    isSubtitleUnderlined: styles?.subtitle?.isUnderlined ?? subtitleDefaults.isUnderlined!,
    subtitleTextAlign: (styles?.subtitle?.textAlign ?? subtitleDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    backgroundColor: styles?.backgroundColor ?? defaults.backgroundColor,
    borderColor: styles?.borderColor ?? defaults.borderColor,
    borderRadius: styles?.borderRadius ?? defaults.borderRadius,
    borderWeight: styles?.borderWeight ?? defaults.borderWeight,
    verticalAlign: (styles?.verticalAlign ?? defaults.verticalAlign) as 'top' | 'center' | 'bottom',
    padding: styles?.padding ?? defaults.padding,
  };

  return result;
}

export function outputFormToNode(formData: OutputFormData, node: INode): INode['data'] {
  return {
    ...node.data,
    title: formData.title,
    subtitle: formData.subtitle,
    varDataOutputId: formData.varDataOutputId,
    fieldType: formData.fieldType,
    styles: {
      ...node.data.styles,
      title: {
        fontFamily: formData.fontFamily,
        fontSize: formData.fontSize,
        isBold: formData.isBold,
        isItalic: formData.isItalic,
        isUnderlined: formData.isUnderlined,
        textAlign: formData.textAlign,
        color: formData.titleColor,
      },
      subtitle: {
        fontFamily: formData.subtitleFontFamily,
        fontSize: formData.subtitleFontSize,
        isBold: formData.isSubtitleBold,
        isItalic: formData.isSubtitleItalic,
        isUnderlined: formData.isSubtitleUnderlined,
        textAlign: formData.subtitleTextAlign,
        color: formData.subtitleColor,
      },
      backgroundColor: formData.backgroundColor,
      borderColor: formData.borderColor,
      borderRadius: formData.borderRadius,
      borderWeight: formData.borderWeight,
      verticalAlign: formData.verticalAlign,
      padding: formData.padding,
    },
  };
}

export function imageNodeToForm(node: INode): ImageFormData {
  const styles = node.data.styles;
  const defaults = defaultImageNodeData.styles;

  const result: ImageFormData = {
    url: node.data.url ?? defaultImageNodeData.url!,
    backgroundColor: styles?.backgroundColor ?? defaults.backgroundColor,
    borderColor: styles?.borderColor ?? defaults.borderColor,
    borderRadius: styles?.borderRadius ?? defaults.borderRadius,
    borderWeight: styles?.borderWeight ?? defaults.borderWeight,
    verticalAlign: (styles?.verticalAlign ?? defaults.verticalAlign) as 'top' | 'center' | 'bottom',
    padding: styles?.padding ?? defaults.padding,
    objectFit: (styles?.image?.objectFit ?? 'cover') as 'cover' | 'contain',
  };

  return result;
}

export function imageFormToNode(formData: ImageFormData, node: INode): INode['data'] {
  return {
    ...node.data,
    url: formData.url,
    styles: {
      ...node.data.styles,
      image: {
        objectFit: formData.objectFit,
      },
      backgroundColor: formData.backgroundColor,
      borderColor: formData.borderColor,
      borderRadius: formData.borderRadius,
      borderWeight: formData.borderWeight,
      verticalAlign: formData.verticalAlign,
      padding: formData.padding,
    },
  };
}

export function submitNodeToForm(node: INode): SubmitFormData {
  const styles = node.data.styles;
  const defaults = defaultSubmitNodeData.styles;
  const submitDefaults = defaults.submit!;

  const result: SubmitFormData = {
    removeOrRetain: node.data.removeOrRetain ?? defaultSubmitNodeData.removeOrRetain!,
    redirectPageId: node.data.redirectPageId,
    fontFamily: styles?.submit?.fontFamily ?? submitDefaults.fontFamily!,
    fontSize: styles?.submit?.fontSize ?? submitDefaults.fontSize!,
    isBold: styles?.submit?.isBold ?? submitDefaults.isBold!,
    isItalic: styles?.submit?.isItalic ?? submitDefaults.isItalic!,
    isUnderlined: styles?.submit?.isUnderlined ?? submitDefaults.isUnderlined!,
    textAlign: (styles?.submit?.textAlign ?? submitDefaults.textAlign!) as 'left' | 'center' | 'right' | 'justify',
    buttonColor: styles?.submit?.color ?? submitDefaults.color!,
    buttonBackgroundColor: styles?.submit?.buttonBackgroundColor ?? submitDefaults.buttonBackgroundColor!,
    backgroundColor: styles?.backgroundColor ?? defaults.backgroundColor,
    borderColor: styles?.borderColor ?? defaults.borderColor,
    borderRadius: styles?.borderRadius ?? defaults.borderRadius,
    borderWeight: styles?.borderWeight ?? defaults.borderWeight,
    verticalAlign: (styles?.verticalAlign ?? defaults.verticalAlign) as 'top' | 'center' | 'bottom',
    padding: styles?.padding ?? defaults.padding,
  };

  return result;
}

export function submitFormToNode(formData: SubmitFormData, node: INode): INode['data'] {
  return {
    ...node.data,
    removeOrRetain: formData.removeOrRetain,
    redirectPageId: formData.redirectPageId,
    styles: {
      ...node.data.styles,
      submit: {
        fontFamily: formData.fontFamily,
        fontSize: formData.fontSize,
        isBold: formData.isBold,
        isItalic: formData.isItalic,
        isUnderlined: formData.isUnderlined,
        textAlign: formData.textAlign,
        color: formData.buttonColor,
        buttonBackgroundColor: formData.buttonBackgroundColor,
      },
      backgroundColor: formData.backgroundColor,
      borderColor: formData.borderColor,
      borderRadius: formData.borderRadius,
      borderWeight: formData.borderWeight,
      verticalAlign: formData.verticalAlign,
      padding: formData.padding,
    },
  };
}

export const nodeToFormTransformers: Record<ITypeNode, NodeToFormTransformer<any> | null> = {
  [ITypeNode.TITLE]: titleNodeToForm,
  [ITypeNode.SHORT_TEXT]: shortTextNodeToForm,
  [ITypeNode.IMAGE]: imageNodeToForm,
  [ITypeNode.INPUT]: inputNodeToForm,
  [ITypeNode.LIST]: listNodeToForm,
  [ITypeNode.OUTPUT]: outputNodeToForm,
  [ITypeNode.SUBMIT]: submitNodeToForm,
};

export const formToNodeTransformers: Record<ITypeNode, FormToNodeTransformer<any> | null> = {
  [ITypeNode.TITLE]: titleFormToNode,
  [ITypeNode.SHORT_TEXT]: shortTextFormToNode,
  [ITypeNode.IMAGE]: imageFormToNode,
  [ITypeNode.INPUT]: inputFormToNode,
  [ITypeNode.LIST]: listFormToNode,
  [ITypeNode.OUTPUT]: outputFormToNode,
  [ITypeNode.SUBMIT]: submitFormToNode,
};

